import { type Signal, useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { readTextFile } from "~/web/src/api";
import type { Channel } from "~/web/src/channel";
import { errorText } from "~/web/src/errors";

// One text file's disk state for an editor: content via GET (with its
// X-File-Version), write = write_file with that baseVersion. A conflict, or a
// change on disk while the editor is clean, refetches; a change while dirty is
// only announced (the user's edit is never thrown away). The editor owns its
// draft and says whether it is dirty.

export interface FileState {
  disk: Signal<string>;
  version: Signal<number>;
  notice: Signal<string | null>;
  busy: Signal<boolean>;
  load(): Promise<void>;
  /** True when written; false leaves the reason in `notice`. */
  write(content: string): Promise<boolean>;
}

export function useFile(channel: Channel, name: string, isDirty: () => boolean): FileState {
  const disk = useSignal("");
  const version = useSignal(0);
  const notice = useSignal<string | null>(null);
  const busy = useSignal(false);

  // Every caller fires and forgets; a failed fetch (network, file gone) lands in the notice.
  async function load() {
    try {
      const r = await readTextFile(channel.id, name);
      disk.value = r.text;
      version.value = r.version;
    } catch (e) {
      notice.value = errorText(e);
    }
  }
  useEffect(() => {
    void load();
    return channel.onFile((n, entry) => {
      if (n !== name) return;
      if (!entry) {
        notice.value = "removed on disk";
        return;
      }
      if (entry.version === version.value) return;
      if (isDirty()) {
        notice.value = `changed on disk (v${entry.version}) — saving overwrites, Reload discards yours`;
      } else {
        void load();
      }
    });
  }, [name]);

  async function write(content: string): Promise<boolean> {
    busy.value = true;
    notice.value = null;
    const r = await channel.send({
      kind: "write_file",
      name,
      baseVersion: version.value,
      content,
    });
    busy.value = false;
    if (r.ok) {
      disk.value = content;
      version.value = r.version ?? version.value;
      return true;
    }
    if (r.error === "conflict") {
      notice.value = "conflict: the file changed underneath you — reloaded";
      await load();
    } else {
      notice.value = `write failed: ${r.error}`;
    }
    return false;
  }

  return { disk, version, notice, busy, load, write };
}
